/** My Queue, dashboard, matrix editor and audit trail. */
import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Loader2, ShieldCheck, Download, Plus, Trash2, GripVertical, Save, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useNfaTheme, money, day, stamp } from './nfaTheme';
import { Btn, Empty, Field, Input, Panel, Pill, Select, Stat } from './NfaPrimitives';
import { Combo, MoneyStepper, SegToggle, Stepper, UserMultiPicker } from './NfaPickers';
import { NfaWidgetGrid, useNfaOverview } from './NfaAnalytics';
import NfaDrilldownDrawer from './NfaDrilldownDrawer';
import NfaDetailDrawer from './NfaDetailDrawer';

const GROUPS = [['overdue', 'Overdue'], ['dueToday', 'Due today'],
                ['thisWeek', 'Due this week'], ['later', 'Later']];

export const NfaQueue = ({ projectId }) => {
  const T = useNfaTheme();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => {
    api.get(`/nfa/queue/mine${projectId ? `?project_id=${projectId}` : ''}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const quickAct = async (row, action) => {
    setBusy(row.nfa_id);
    try {
      await api.post(`/nfa/${row.nfa_id}/action`, {
        level_no: row.level_no, action,
        comment: action === 'APPROVED' ? 'Approved from queue' : 'Returned from queue' });
      toast.success(`${row.nfa_no} ${action.toLowerCase()}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  return (
    <div className="space-y-4" data-testid="nfa-queue">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {GROUPS.map(([k, label]) => (
          <Stat key={k} T={T} label={label} value={data.counts[k]} testId={`nfa-queue-count-${k}`}
            tone={k === 'overdue' && data.counts[k] ? T.red : T.ink} />
        ))}
      </div>
      {data.total === 0 && (
        <Panel T={T} testId="nfa-queue-empty">
          <Empty T={T}>Nothing is waiting on you. Approvals you are assigned to will appear here.</Empty>
        </Panel>
      )}
      {GROUPS.filter(([k]) => data.groups[k].length).map(([k, label]) => (
        <Panel key={k} T={T} pad={false} title={`${label} — ${data.groups[k].length}`}
          testId={`nfa-queue-group-${k}`}>
          {data.groups[k].map((row) => (
            <div key={row.nfa_id} className="px-4 py-2.5 flex items-center gap-3 flex-wrap text-[11.5px]"
              style={{ borderTop: `1px solid ${T.border}` }} data-testid={`nfa-queue-row-${row.nfa_no}`}>
              <button type="button" onClick={() => setOpen(row.nfa_id)} className="min-w-0 text-left">
                <div className="font-mono text-[10.5px] tabular-nums" style={{ color: T.primary }}>{row.nfa_no}</div>
                <div className="truncate" style={{ color: T.ink }}>{row.title}</div>
                <div className="text-[10px]" style={{ color: T.muted }}>
                  {row.project_name || row.project_id} · {row.nfa_type} · raised by {row.raised_by_name}
                </div>
              </button>
              <div className="ml-auto flex items-center gap-3 flex-wrap tabular-nums text-[10.5px]"
                   style={{ color: T.muted }}>
                <span className="font-semibold" style={{ color: T.ink }}>
                  {money(row.amount, row.currency)}</span>
                <span>level {row.level_no} · {row.total_levels} in route · {row.approver_role}</span>
                <span style={{ color: row.is_overdue ? T.red : T.body }}>
                  {row.days_pending}d pending · SLA {row.sla_days}d</span>
                {row.is_overdue && <Pill value="OVERDUE" T={T} />}
                <Btn T={T} variant="ok" disabled={busy === row.nfa_id}
                  testId={`nfa-queue-approve-${row.nfa_no}`} onClick={() => quickAct(row, 'APPROVED')}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                </Btn>
                <Btn T={T} variant="warn" disabled={busy === row.nfa_id}
                  testId={`nfa-queue-return-${row.nfa_no}`} onClick={() => quickAct(row, 'RETURNED')}>
                  Return
                </Btn>
              </div>
            </div>
          ))}
        </Panel>
      ))}
      {open && <NfaDetailDrawer nfaId={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

const DASH_WIDGETS = ['ageing', 'pendingByLevel', 'cycleByLevel', 'roleWorkload',
                      'slaOutcome', 'decisions', 'byStatus', 'byType'];

export const NfaDashboard = ({ projectId, projectName }) => {
  const T = useNfaTheme();
  const { d, busy, reload } = useNfaOverview(projectId);
  const [drill, setDrill] = useState(null);
  if (busy && !d) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (!d) return <Panel T={T}><Empty T={T}>Pending-approval analytics could not be loaded.</Empty></Panel>;
  const k = d.kpis;
  const go = (view) => setDrill({ view });
  const CARDS = [
    ['Open NFAs', k.open, `${k.pendingLevels} level(s) awaiting action`, T.ink, 'open'],
    ['Open value', money(k.openValue), `of ${money(k.value)} in the register`, T.ink, 'open'],
    ['SLA breaches', k.slaBreaches, `compliance ${k.slaCompliance ?? '—'}%`,
     k.slaBreaches ? T.red : T.green, 'sla_breach'],
    ['Value at risk', money(k.valueAtRisk), `${k.notesAtRisk} note(s) past SLA`,
     k.valueAtRisk ? T.red : T.green, 'at_risk'],
    ['Bottleneck level', k.bottleneckLevel ? `L${k.bottleneckLevel.level_no}` : '—',
     k.bottleneckLevel ? `${k.bottleneckLevel.avgDays}d average` : null, T.accent, 'bottleneck'],
    ['Oldest pending', k.oldestPendingDays ? `${k.oldestPendingDays}d` : '—',
     `${k.pendingOver14} beyond 14 days`, k.oldestPendingDays > 14 ? T.red : T.ink, 'oldest_pending'],
    ['Avg cycle time', k.avgCycleDays != null ? `${k.avgCycleDays}d` : '—',
     `median ${k.medianCycleDays ?? '—'}d`, T.ink, 'slow_cycle'],
    ['Approved, not committed', k.approvedNotCommitted, money(k.approvedNotCommittedValue),
     T.primary, 'approved_not_committed'],
  ];
  return (
    <div className="space-y-4" data-testid="nfa-dashboard">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10.5px] px-2 py-1 rounded-md" data-testid="nfa-dash-scope"
          style={{ background: T.tint(T.primary), color: T.primary }}>
          {projectName || projectId} · every widget drills L1 → L2 → L3 → L4
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Btn T={T} variant="warn" testId="nfa-dash-remind-all" onClick={async () => {
            try {
              const r = await api.post(`/nfa/assist/reminders/run?project_id=${projectId || ''}`);
              toast.success(`${r.data.sentCount} reminder(s) sent · ${r.data.skipped} skipped`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Could not run the reminder sweep');
            }
          }}>
            <BellRing className="w-3.5 h-3.5" /> Nudge every overdue approver
          </Btn>
          <Btn T={T} variant="ghost" testId="nfa-dash-refresh" onClick={reload}>Refresh</Btn>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {CARDS.map(([label, value, sub, tone, view]) => (
          <button type="button" key={label} onClick={() => go(view)}
            className="text-left hover:opacity-85 transition-opacity"
            data-testid={`nfa-dash-card-${String(label).toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <Stat T={T} label={label} value={value} sub={sub} tone={tone} />
          </button>
        ))}
      </div>
      <NfaWidgetGrid widgets={d.widgets} only={DASH_WIDGETS} T={T} projectId={projectId}
        trend={d.trend} />
      {drill && (
        <NfaDrilldownDrawer view={drill.view} projectId={projectId}
          onClose={() => setDrill(null)} />
      )}
    </div>
  );
};

export const NfaMatrix = ({ meta }) => {
  const T = useNfaTheme();
  const activeProject = meta?.activeProject || meta?.projects?.[0] || null;
  const project = activeProject?.project_id || '';
  const projectName = activeProject?.name || project;
  const [rows, setRows] = useState([]);
  const [assign, setAssign] = useState([]);
  const [type, setType] = useState('Contract Award');
  const [dragId, setDragId] = useState(null);
  const [saving, setSaving] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState('');
  const [tplPick, setTplPick] = useState('');
  const loadTemplates = useCallback(() => {
    api.get('/nfa/assist/matrix-templates')
      .then((r) => setTemplates(r.data.templates || [])).catch(() => setTemplates([]));
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  const [draft, setDraft] = useState({ level_no: 1, role: 'Reviewer', amount_from: 0,
    amount_to: 10000000, sla_days: 3, is_mandatory: true, parallel_mode: 'ALL' });

  const load = useCallback(() => {
    if (!project) return;
    api.get(`/nfa/matrix/rows?project_id=${project}&nfa_type=${encodeURIComponent(type)}`)
      .then((r) => setRows(r.data.rows)).catch(() => setRows([]));
    api.get(`/nfa/roles/assignments?project_id=${project}`)
      .then((r) => setAssign(r.data.assignments)).catch(() => setAssign([]));
  }, [project, type]);
  useEffect(() => { load(); }, [load]);

  const save = async (row) => {
    await api.post('/nfa/matrix/rows', { project_id: project, nfa_type: type, ...row,
      amount_from: Number(row.amount_from), amount_to: Number(row.amount_to),
      sla_days: Number(row.sla_days), is_active: true });
    toast.success(`Level ${row.level_no} saved — the next NFA routes accordingly`);
    load();
  };

  // Inline edit: optimistic in the grid, PATCHed on the server, no form.
  const patch = async (row, field, value) => {
    setRows((rs) => rs.map((r) => (r.matrixId === row.matrixId ? { ...r, [field]: value } : r)));
    setSaving(`${row.matrixId}:${field}`);
    try {
      await api.patch(`/nfa/matrix/rows/${row.matrixId}`, { [field]: value });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save that change');
      load();
    } finally { setSaving(null); }
  };

  const reorder = async (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const order = rows.map((r) => r.matrixId);
    const next = order.filter((x) => x !== fromId);
    next.splice(order.indexOf(toId), 0, fromId);
    setRows(next.map((id, i) => ({ ...rows.find((r) => r.matrixId === id), level_no: i + 1 })));
    try {
      const r = await api.post('/nfa/matrix/reorder',
        { project_id: project, nfa_type: type, matrix_ids: next });
      setRows(r.data.rows);
      toast.success('Levels renumbered');
    } catch { toast.error('Could not reorder the levels'); load(); }
  };

  const roleOptions = (meta?.roles || []).map((r) => ({ value: r, label: r }));

  return (
    <div className="space-y-4" data-testid="nfa-matrix">
      <Panel T={T} title="Approval matrix" testId="nfa-matrix-panel" pad={false}
        subtitle="Every cell is clickable — change a role, band, SLA or mode and the next NFA routes differently. Drag a row to renumber the levels."
        right={<>
          <span className="text-[10.5px] px-2.5 py-1.5 rounded-md" data-testid="nfa-matrix-project"
            style={{ background: T.isDark ? T.page : '#F4F6FA', border: `1px dashed ${T.border}`,
                     color: T.body }}>{projectName || '— no project —'}</span>
          <div className="w-52">
            <Combo T={T} value={type} onChange={setType} testId="nfa-matrix-type"
              options={(meta?.types || []).map((t) => ({ value: t, label: t }))} />
          </div>
        </>}>
        <div className="grid grid-cols-12 px-4 py-2 text-[9.5px] uppercase tracking-[0.08em]"
          style={{ background: T.surfaceAlt, color: T.muted }}>
          <div className="col-span-1">Level</div><div className="col-span-2">Role</div>
          <div className="col-span-4">Amount band</div><div className="col-span-2">SLA</div>
          <div className="col-span-2">Mode</div><div className="col-span-1">Required</div>
        </div>
        {rows.length === 0 ? <Empty T={T} testId="nfa-matrix-empty">No levels configured for this combination.</Empty>
          : rows.map((r) => (
            <div key={r.matrixId} draggable onDragStart={() => setDragId(r.matrixId)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { reorder(dragId, r.matrixId); setDragId(null); }}
              className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[11.5px] items-center"
              style={{ borderTop: `1px solid ${T.border}`,
                       opacity: dragId === r.matrixId ? 0.5 : 1,
                       background: saving?.startsWith(r.matrixId) ? T.tint(T.primary) : 'transparent' }}
              data-testid={`nfa-matrix-row-${r.level_no}`}>
              <div className="col-span-1 flex items-center gap-1.5">
                <GripVertical className="w-3.5 h-3.5 cursor-grab shrink-0" style={{ color: T.muted }} />
                <span className="tabular-nums font-semibold" style={{ color: T.primary }}>{r.level_no}</span>
              </div>
              <div className="col-span-2">
                <Combo T={T} value={r.role} options={roleOptions} testId={`nfa-matrix-role-${r.level_no}`}
                  onChange={(v) => patch(r, 'role', v)} />
              </div>
              <div className="col-span-4 grid grid-cols-2 gap-2">
                <MoneyStepper T={T} value={r.amount_from} compact testId={`nfa-matrix-from-${r.level_no}`}
                  onChange={(v) => patch(r, 'amount_from', v)} />
                <MoneyStepper T={T} value={r.amount_to} allowInfinite compact
                  testId={`nfa-matrix-to-${r.level_no}`}
                  onChange={(v) => patch(r, 'amount_to', v)} />
              </div>
              <div className="col-span-2">
                <Stepper T={T} value={r.sla_days} min={1} max={30} suffix="d"
                  testId={`nfa-matrix-sla-${r.level_no}`} onChange={(v) => patch(r, 'sla_days', v)} />
              </div>
              <div className="col-span-2">
                <SegToggle T={T} value={r.parallel_mode || 'ALL'} testId={`nfa-matrix-mode-${r.level_no}`}
                  onChange={(v) => patch(r, 'parallel_mode', v)}
                  options={[{ value: 'ALL', label: 'All' }, { value: 'ANY', label: 'Any one' }]} />
              </div>
              <div className="col-span-1 flex items-center gap-1.5 justify-end">
                <SegToggle T={T} value={r.is_mandatory ? 'Y' : 'N'}
                  testId={`nfa-matrix-mandatory-${r.level_no}`}
                  onChange={(v) => patch(r, 'is_mandatory', v === 'Y')}
                  options={[{ value: 'Y', label: 'Must', tone: T.primary },
                            { value: 'N', label: 'Opt', tone: T.muted }]} />
                <button type="button" data-testid={`nfa-matrix-delete-${r.level_no}`}
                  onClick={async () => { await api.delete(`/nfa/matrix/rows/${r.matrixId}`); load(); }}>
                  <Trash2 className="w-3.5 h-3.5" style={{ color: T.muted }} />
                </button>
              </div>
            </div>
          ))}
      </Panel>

      <Panel T={T} title="Add or replace a level" testId="nfa-matrix-editor"
        subtitle="Pick, step and toggle — nothing here needs typing">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <Field label="Level" T={T}>
            <Stepper T={T} value={draft.level_no} min={1} max={12} testId="nfa-matrix-level"
              onChange={(v) => setDraft({ ...draft, level_no: v })} />
          </Field>
          <Field label="Role" T={T}>
            <Combo T={T} value={draft.role} options={roleOptions} testId="nfa-matrix-role"
              onChange={(v) => setDraft({ ...draft, role: v })} />
          </Field>
          <Field label="Band from" T={T}>
            <MoneyStepper T={T} value={draft.amount_from} testId="nfa-matrix-from"
              onChange={(v) => setDraft({ ...draft, amount_from: v })} />
          </Field>
          <Field label="Band to" T={T}>
            <MoneyStepper T={T} value={draft.amount_to} allowInfinite testId="nfa-matrix-to"
              onChange={(v) => setDraft({ ...draft, amount_to: v })} />
          </Field>
          <Field label="SLA days" T={T}>
            <Stepper T={T} value={draft.sla_days} min={1} max={30} suffix="d" testId="nfa-matrix-sla"
              onChange={(v) => setDraft({ ...draft, sla_days: v })} />
          </Field>
          <div className="space-y-2">
            <SegToggle T={T} value={draft.parallel_mode} testId="nfa-matrix-mode"
              onChange={(v) => setDraft({ ...draft, parallel_mode: v })}
              options={[{ value: 'ALL', label: 'All must approve' }, { value: 'ANY', label: 'Any one' }]} />
            <SegToggle T={T} value={draft.is_mandatory ? 'Y' : 'N'} testId="nfa-matrix-mandatory"
              onChange={(v) => setDraft({ ...draft, is_mandatory: v === 'Y' })}
              options={[{ value: 'Y', label: 'Mandatory', tone: T.primary },
                        { value: 'N', label: 'Optional', tone: T.muted }]} />
            <Btn T={T} onClick={() => save(draft)} testId="nfa-matrix-save">
              <Plus className="w-3.5 h-3.5" /> Save level
            </Btn>
          </div>
        </div>
      </Panel>

      <Panel T={T} title="Reusable approval templates" testId="nfa-matrix-templates"
        subtitle="Save this ladder once, then apply it to any project and NFA type instead of rebuilding it">
        <div className="flex items-end gap-3 flex-wrap">
          <Field label="Save the current ladder as" T={T}>
            <Input T={T} value={tplName} onChange={(e) => setTplName(e.target.value)}
              placeholder="Standard CAPEX ladder" data-testid="nfa-tpl-name" />
          </Field>
          <Btn T={T} testId="nfa-tpl-save" onClick={async () => {
            if (!tplName.trim()) { toast.error('Give the template a name'); return; }
            try {
              await api.post('/nfa/assist/matrix-templates',
                { name: tplName.trim(), project_id: project, nfa_type: type });
              toast.success(`Template "${tplName.trim()}" saved`);
              setTplName(''); loadTemplates();
            } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save the template'); }
          }}><Save className="w-3.5 h-3.5" /> Save template</Btn>
          <div className="w-64">
            <Field label="Apply a template to this project and type" T={T}>
              <Combo T={T} value={tplPick} onChange={setTplPick} testId="nfa-tpl-pick"
                placeholder={templates.length ? 'Pick a template…' : 'No templates saved yet'}
                options={templates.map((t) => ({ value: t.templateId, label: t.name,
                  sub: `${t.levelCount} levels` }))} />
            </Field>
          </div>
          <Btn T={T} variant="ghost" testId="nfa-tpl-apply" onClick={async () => {
            if (!tplPick) { toast.error('Pick a template first'); return; }
            try {
              const r = await api.post(`/nfa/assist/matrix-templates/${tplPick}/apply`,
                { project_id: project, nfa_type: type, replace: true });
              setRows(r.data.rows);
              toast.success(`"${r.data.template}" applied — ${r.data.applied.length} levels`);
            } catch (e) { toast.error(e?.response?.data?.detail || 'Could not apply the template'); }
          }}>Apply to {type}</Btn>
        </div>
        {!!templates.length && (
          <div className="mt-3 flex items-center gap-1.5 flex-wrap">
            {templates.map((t) => (
              <span key={t.templateId} className="inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full"
                data-testid={`nfa-tpl-chip-${t.templateId}`}
                style={{ background: T.tint(T.primary), color: T.primary }}>
                {t.name} · {t.levelCount} levels
                <button type="button" data-testid={`nfa-tpl-delete-${t.templateId}`}
                  onClick={async () => {
                    await api.delete(`/nfa/assist/matrix-templates/${t.templateId}`);
                    loadTemplates(); toast.success('Template removed');
                  }}><Trash2 className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </Panel>

      <Panel T={T} pad={false} title="Role assignments" testId="nfa-roles"
        subtitle="Pick people from the directory — the raiser is always excluded under maker-checker.">
        {(meta?.roles || []).map((role) => {
          const row = assign.find((a) => a.role === role);
          return (
            <div key={role} className="px-4 py-2.5 flex items-start gap-3 text-[11.5px]"
              style={{ borderTop: `1px solid ${T.border}` }} data-testid={`nfa-role-${role}`}>
              <span className="w-40 shrink-0 pt-1" style={{ color: T.ink }}>{role}</span>
              <div className="flex-1 min-w-0">
                <UserMultiPicker T={T} projectId={project} testId={`nfa-role-picker-${role}`}
                  value={row?.user_ids || []}
                  onChange={async (ids) => {
                    setAssign((as) => {
                      const next = as.filter((a) => a.role !== role);
                      return [...next, { project_id: project, role, user_ids: ids }]
                        .sort((a, b) => a.role.localeCompare(b.role));
                    });
                    try {
                      await api.post('/nfa/roles/assignments',
                        { project_id: project, role, user_ids: ids });
                      toast.success(`${role} updated`);
                      load();
                    } catch { toast.error(`Could not update ${role}`); load(); }
                  }} />
              </div>
            </div>
          );
        })}
      </Panel>
    </div>
  );
};

export const NfaAudit = ({ projectId, projectName }) => {
  const T = useNfaTheme();
  const [data, setData] = useState(null);
  const [chain, setChain] = useState(null);
  const [f, setF] = useState({ actor_id: '', action: '', source: '', date_from: '', date_to: '' });
  const load = useCallback(() => {
    const qs = new URLSearchParams();
    if (projectId) qs.set('project_id', projectId);
    Object.entries(f).forEach(([k, v]) => { if (v) qs.set(k, v); });
    api.get(`/nfa/audit/events?${qs.toString()}&limit=200`).then((r) => setData(r.data));
  }, [f, projectId]);
  useEffect(() => { load(); api.get('/nfa/audit/verify').then((r) => setChain(r.data)); }, [load]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="space-y-4" data-testid="nfa-audit">
      <Panel T={T} title="Audit trail" testId="nfa-audit-panel" pad={false}
        subtitle={`Append-only, scoped to ${projectName || 'the selected project'}. Each event stores the hash of the previous event, so any tampering is detectable.`}
        right={<>
          {chain && (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10.5px] font-semibold"
              style={{ background: T.tint(chain.valid ? T.green : T.red),
                color: chain.valid ? T.green : T.red }} data-testid="nfa-chain-state">
              <ShieldCheck className="w-3.5 h-3.5" />
              {chain.valid ? `Chain valid · ${chain.checked} events` : `Chain BROKEN at #${chain.brokenAt}`}
            </span>
          )}
          {['csv', 'pdf'].map((fmt) => (
            <a key={fmt} href={`${process.env.REACT_APP_BACKEND_URL}/api/nfa/audit/export?fmt=${fmt}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold"
              style={{ color: T.body, border: `1px solid ${T.border}` }}
              data-testid={`nfa-audit-export-${fmt}`}>
              <Download className="w-3.5 h-3.5" /> {fmt.toUpperCase()}
            </a>
          ))}
        </>}>
        <div className="p-3 grid grid-cols-2 md:grid-cols-5 gap-2">
          <Input T={T} placeholder="Actor user id" value={f.actor_id} onChange={set('actor_id')}
            data-testid="nfa-audit-actor" />
          <Select T={T} value={f.action} onChange={set('action')} data-testid="nfa-audit-action">
            <option value="">All actions</option>
            {(data?.actions || []).map((a) => <option key={a}>{a}</option>)}
          </Select>
          <Select T={T} value={f.source} onChange={set('source')} data-testid="nfa-audit-source">
            <option value="">All sources</option>
            {(data?.sources || []).map((s) => <option key={s}>{s}</option>)}
          </Select>
          <Input T={T} type="date" value={f.date_from} onChange={set('date_from')} data-testid="nfa-audit-from" />
          <Input T={T} type="date" value={f.date_to} onChange={set('date_to')} data-testid="nfa-audit-to" />
        </div>
        <div className="grid grid-cols-12 px-4 py-2 text-[9.5px] uppercase tracking-[0.08em]"
          style={{ background: T.surfaceAlt, color: T.muted }}>
          <div className="col-span-1">#</div><div className="col-span-2">When</div>
          <div className="col-span-2">Action</div><div className="col-span-2">Entity</div>
          <div className="col-span-2">Actor</div><div className="col-span-1">Source</div>
          <div className="col-span-2">Hash</div>
        </div>
        {(data?.events || []).map((e) => (
          <div key={e.eventId} className="grid grid-cols-12 px-4 py-1.5 text-[10.5px] items-center"
            style={{ borderTop: `1px solid ${T.border}` }} data-testid={`nfa-audit-row-${e.seq}`}>
            <div className="col-span-1 tabular-nums" style={{ color: T.muted }}>{e.seq}</div>
            <div className="col-span-2 tabular-nums" style={{ color: T.body }}>{stamp(e.timestamp)}</div>
            <div className="col-span-2 font-semibold" style={{ color: T.ink }}>{e.action}</div>
            <div className="col-span-2 truncate font-mono" style={{ color: T.body }}>
              {e.entity_type}:{String(e.entity_id).slice(-8)}</div>
            <div className="col-span-2 truncate" style={{ color: T.body }}>{e.actor_role} · {e.actor_id}</div>
            <div className="col-span-1"><Pill value={e.source} T={T} /></div>
            <div className="col-span-2 font-mono truncate" style={{ color: T.muted }}
              title={`hash ${e.hash}\nprev ${e.prev_hash}`}>{String(e.hash).slice(0, 14)}…</div>
          </div>
        ))}
        {data && data.events.length === 0 && <Empty T={T}>No audit events match these filters.</Empty>}
      </Panel>
    </div>
  );
};
